import { InvoiceItem } from "../types"

/** Resolves line amount: quantity * rate, or legacy stored amount when rate was never set. */
export const getItemAmount = (item: InvoiceItem): number => {
  const qty = item.quantity != null && item.quantity !== '' ? parseFloat(item.quantity.toString()) : 1
  if (item.rate != null && item.rate !== '') {
    const rate = parseFloat(item.rate.toString())
    return Number.isNaN(rate) ? 0 : qty * rate
  }
  if (item.rate === '') {
    return 0
  }
  if (item.amount != null && item.amount !== '') {
    const amount = parseFloat(item.amount.toString())
    return Number.isNaN(amount) ? 0 : amount
  }
  return 0
}

/** Display value for read-only line amount fields (never blank for zero). */
export const formatItemAmountForDisplay = (item: InvoiceItem): number => {
  return parseFloatValue(getItemAmount(item)) ?? 0
}

export const roundMoney = (value: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export const getFloatValueFromString = (stringValue = '') => {
  if(!stringValue || stringValue.trim() === '') {
    stringValue = '0'
  }
  const value = parseFloatValue(stringValue)
  return value
}

export const calculateSubtotal = (invoiceItems: InvoiceItem[]): number => {
  const subtotal = invoiceItems.reduce((sum, item) => sum + getItemAmount(item), 0)
  return roundMoney(subtotal)
}

export const calculateTax = (invoiceItems: InvoiceItem[]): number => {
  const totalTaxAmount = invoiceItems.reduce((sum, item) => {
    const amount = getItemAmount(item)
    const taxAmount = item.taxPercentage ? amount * (parseFloat(item.taxPercentage.toString()) / 100) : 0
    return sum + taxAmount
  }, 0)
  return roundMoney(totalTaxAmount)
}

/** Subtotal plus tax (when enabled), each rounded to cents so the parts sum to the total. */
export const calculateTotal = (invoiceItems: InvoiceItem[], isTaxed: boolean): number => {
  const subtotal = calculateSubtotal(invoiceItems)
  const tax = isTaxed ? calculateTax(invoiceItems) : 0
  return roundMoney(subtotal + tax)
}

export const computeInvoiceTotals = (
  invoiceItems: InvoiceItem[],
  isTaxed: boolean,
): { total: number; totalTax: number } => ({
  total: calculateTotal(invoiceItems, isTaxed),
  totalTax: isTaxed ? calculateTax(invoiceItems) : 0,
})

/** True when the invoice total (including tax if enabled) is zero. */
export const isInvoiceTotalZero = (invoiceItems: InvoiceItem[], isTaxed: boolean): boolean =>
  Math.abs(calculateTotal(invoiceItems, isTaxed)) < 0.005

export const flattenObject = (obj: any, prefix = '') => {
  return Object.entries(obj).reduce((acc: any, [key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key
    if (typeof value === 'object' && value !== null) {
      Object.assign(acc, flattenObject(value, newKey))
    } else {
      acc[newKey] = value
    }
    return acc
  }, {})
}

/**
 * Parses a string or number to a float value capped to 2 decimal places.
 */
export const parseFloatValue = (value: string | number | undefined | null) => {
  if (value === undefined || value === null || value === '') return undefined
  const parsed = Number(value)
  if (Number.isNaN(parsed)) return undefined
  return roundMoney(parsed)
}

/**
 * Gets the last number in a given string.
 * If no nuber is found will return null
 * For example: itemInvoiceAmount99 will return 99
 * @param str 
 * @returns 
 */
export const getLastDigitOfString = (str: string): number | null => {
  const match = str.match(/\d+$/)
  if(match) {
    return parseInt(match[0])
  } else {
    return null
  }
}